"use client"

import { Fragment, useEffect, useState, memo } from "react"
import MainLayout from "@/layout/MainLayout"
import StandardCard from "@/components/cards/StandardCard"
import { LISTING_GRID_CLASS, ListingSectionAd } from "@/components/ListingGridWithAds"
import CategoryUnderline from "@/components/ui/CategoryUnderline"
import PageHeader from "@/components/ui/PageHeader"
import axiosInstance from "@/lib/axios"
import { CityWisePageSkeleton, EmptyState } from "@/components/ui/Skeleton"

const DistrictNews = ({ initialData = null }) => {
    const [sections, setSections] = useState(() => initialData?.sections || [])
    const [loading, setLoading] = useState(() => initialData == null)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (initialData?.sections) {
            setSections(initialData.sections)
            setLoading(false)
            return
        }

        const fetchCityWiseNews = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await axiosInstance.get('/news/city-wise')
                if (response.data.success) {
                    setSections(response.data.data || [])
                } else {
                    setError(response.data.error || 'बातम्या लोड करण्यात अडचण आली.')
                }
            } catch (err) {
                console.error("Error fetching city-wise news:", err)
                setError('बातम्या लोड करण्यात अडचण आली.')
            } finally {
                setLoading(false)
            }
        }

        fetchCityWiseNews()
    }, [initialData])

    return (
        <MainLayout>
            <PageHeader
                title="शहरानुसार बातम्या"
                description="संपूर्ण महाराष्ट्रातील शहरांतील बातम्या."
                accentName="Cities"
            />

            {loading ? (
                <CityWisePageSkeleton />
            ) : error ? (
                <EmptyState message={error} />
            ) : sections.length > 0 ? (
                <div className="space-y-8 md:space-y-10">
                    {sections.map((section, index) => {
                        // After 1st city, then every 2 cities (indices 0, 2, 4, …)
                        const showAdAfterCity = index % 2 === 0
                        const adPosition = index % 4 === 0 ? 'top' : 'middle'

                        return (
                            <Fragment key={section.city.key}>
                                <section>
                                    <CategoryUnderline
                                        name={section.city.name}
                                        label={section.city.name}
                                        url={`/city/${section.city.slug}`}
                                    />

                                    <div className={LISTING_GRID_CLASS}>
                                        {section.news.map((item) => (
                                            <StandardCard
                                                key={item.id}
                                                layout="col"
                                                headline={item.title}
                                                imageUrl={item.featuredImage}
                                                data={item}
                                            />
                                        ))}
                                    </div>
                                </section>

                                {showAdAfterCity && (
                                    <ListingSectionAd
                                        page="city_updates"
                                        position={adPosition}
                                    />
                                )}
                            </Fragment>
                        )
                    })}
                </div>
            ) : (
                <EmptyState message="सध्या शहरानुसार बातम्या उपलब्ध नाहीत." />
            )}
        </MainLayout>
    )
}

export default memo(DistrictNews)
